<template>
  <div class="presentation-viewer">
    <!-- Viewer Header -->
    <div class="viewer-header">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h6 class="mb-0">
            {{ currentPresentation ? currentPresentation.original_filename : 'No Presentation' }}
          </h6>
          <small v-if="currentPresentation" class="text-muted">
            Page {{ currentPage }} of {{ totalPages }}
          </small>
        </div>

        <!-- Teacher Controls -->
        <div v-if="role === 'teacher' && currentPresentation" class="d-flex align-items-center">
          <b-button-group size="sm" class="mr-2">
            <b-button
              variant="outline-primary"
              @click="previousPage"
              :disabled="currentPage <= 1"
            >
              <feather-icon icon="ChevronLeftIcon" size="16" />
            </b-button>
            <b-button variant="outline-primary" disabled>
              {{ currentPage }} / {{ totalPages }}
            </b-button>
            <b-button
              variant="outline-primary"
              @click="nextPage"
              :disabled="currentPage >= totalPages"
            >
              <feather-icon icon="ChevronRightIcon" size="16" />
            </b-button>
          </b-button-group>

          <b-button-group size="sm">
            <b-button
              variant="outline-secondary"
              @click="zoomOut"
              :disabled="zoomLevel <= 0.5"
            >
              <feather-icon icon="ZoomOutIcon" size="16" />
            </b-button>
            <b-button variant="outline-secondary" disabled>
              {{ Math.round(zoomLevel * 100) }}%
            </b-button>
            <b-button
              variant="outline-secondary"
              @click="zoomIn"
              :disabled="zoomLevel >= 2.0"
            >
              <feather-icon icon="ZoomInIcon" size="16" />
            </b-button>
          </b-button-group>
        </div>
      </div>
    </div>

    <!-- Viewer Content -->
    <div class="viewer-content">
      <!-- No Presentation State -->
      <div v-if="!currentPresentation" class="no-presentation">
        <div class="text-center py-5">
          <feather-icon icon="FileTextIcon" size="48" class="text-muted mb-3" />
          <h5 class="text-muted">No presentation loaded</h5>
          <p class="text-muted mb-3">
            {{ role === 'teacher' ? 'Upload a presentation to get started' : 'Waiting for teacher to upload presentation' }}
          </p>

          <!-- Teacher Upload Button -->
          <div v-if="role === 'teacher'">
            <b-button
              variant="primary"
              @click="scrollToUpload"
            >
              <feather-icon icon="UploadIcon" size="16" class="mr-1" />
              Upload Presentation
            </b-button>
            <p class="text-muted small mt-3">
              PowerPoint (.pptx, .ppt) or PDF files • Max 50MB
            </p>
          </div>
        </div>
      </div>

      <!-- PDF Viewer -->
      <div v-else class="pdf-container">
        <div class="pdf-viewer-wrapper" ref="pdfViewerWrapper">
          <canvas
            ref="pdfCanvas"
            class="pdf-canvas"
            :style="{
              transform: `scale(${zoomLevel})`,
              transformOrigin: 'top left'
            }"
          ></canvas>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="pdf-loading">
          <div class="text-center py-5">
            <b-spinner variant="primary" class="mb-3"></b-spinner>
            <p class="text-muted">Loading presentation...</p>
          </div>
        </div>

        <!-- Error State -->
        <div v-if="loadError" class="alert alert-danger">
          <feather-icon icon="AlertCircleIcon" size="16" class="mr-1" />
          {{ loadError }}
          <b-button variant="link" size="sm" @click="retryLoad" class="ml-2">
            Retry
          </b-button>
        </div>
      </div>

      <!-- Student Navigation Info -->
      <div v-if="role === 'student' && currentPresentation" class="student-info">
        <small class="text-muted d-flex align-items-center">
          <feather-icon icon="InfoIcon" size="14" class="mr-1" />
          Teacher controls the presentation navigation
        </small>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'

export default {
  name: 'PresentationViewer',
  props: {
    webinarId: {
      type: [String, Number],
      required: true
    },
    userRole: {
      type: String,
      default: 'student'
    }
  },
  data() {
    return {
      pdfDocument: null,
      pdfPage: null,
      isLoading: false,
      loadError: null,
      zoomLevel: 1.0,
      canvas: null,
      context: null
    }
  },
  computed: {
    ...mapGetters('lessonRoom', ['currentPresentation']),
    ...mapState('lessonRoom', ['currentPage', 'totalPages']),
    role() {
      // Use the userRole prop for role-based rendering
      return this.userRole
    }
  },
  watch: {
    currentPresentation: {
      handler(newVal) {
        if (newVal) {
          this.loadPDF()
        } else {
          this.clearPDF()
        }
      },
      immediate: true
    },
    currentPage(newVal) {
      if (this.pdfDocument && newVal) {
        this.renderPage(newVal)
      }
    },
    zoomLevel() {
      if (this.currentPage) {
        this.renderPage(this.currentPage)
      }
    }
  },
  methods: {
    ...mapActions('lessonRoom', [
      'changePresentationPage',
      'loadPresentationPDF'
    ]),

    async loadPDF() {
      if (!this.currentPresentation) {
        console.warn('No current presentation to load')
        return
      }

      if (!this.currentPresentation.pdf_url) {
        console.error('Presentation has no PDF URL:', this.currentPresentation)
        this.loadError = 'Presentation PDF URL is missing'
        return
      }

      this.isLoading = true
      this.loadError = null

      try {
        console.log('Loading PDF from URL:', this.currentPresentation.pdf_url)

        // Dynamically import PDF.js
        const pdfjsLib = await this.loadPDFJS()

        // Load PDF document
        const loadingTask = pdfjsLib.getDocument({
          url: this.currentPresentation.pdf_url,
          cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/cmaps/',
          cMapPacked: true,
          withCredentials: false
        })

        this.pdfDocument = await loadingTask.promise

        console.log('PDF loaded, total pages:', this.pdfDocument.numPages)

        // Initialize canvas
        this.canvas = this.$refs.pdfCanvas
        this.context = this.canvas.getContext('2d')

        if (!this.canvas || !this.context) {
          throw new Error('Canvas not available')
        }

        // Render first page or current page
        const pageToRender = this.currentPage || 1
        console.log('Rendering page:', pageToRender)
        await this.renderPage(pageToRender)

        console.log('PDF loaded successfully:', this.currentPresentation.original_filename)

      } catch (error) {
        console.error('Error loading PDF:', error)
        if (error.name === 'MissingPDFException') {
          this.loadError = 'PDF file not found. Please try uploading again.'
        } else if (error.message && error.message.includes('CORS')) {
          this.loadError = 'Unable to load PDF due to security restrictions.'
        } else {
          this.loadError = 'Failed to load presentation: ' + (error.message || 'Unknown error')
        }
      } finally {
        this.isLoading = false
      }
    },

    async loadPDFJS() {
      // Load PDF.js dynamically
      if (window.pdfjsLib) {
        return window.pdfjsLib
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/build/pdf.min.js'
        script.onload = () => {
          // Configure PDF.js worker
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/build/pdf.worker.min.js'
          resolve(window.pdfjsLib)
        }
        script.onerror = reject
        document.head.appendChild(script)
      })
    },

    async renderPage(pageNumber) {
      if (!this.pdfDocument || !this.canvas || !this.context) {
        console.warn('Cannot render page: missing pdfDocument, canvas, or context')
        return
      }

      try {
        console.log('Rendering page:', pageNumber, 'of', this.pdfDocument.numPages)

        // Get page
        this.pdfPage = await this.pdfDocument.getPage(pageNumber)

        // Get viewport
        const viewport = this.pdfPage.getViewport({ scale: 1.0 })
        console.log('Original viewport size:', viewport.width, 'x', viewport.height)

        // Set canvas dimensions
        const containerWidth = this.$refs.pdfViewerWrapper.clientWidth - 40 // Account for padding
        const scale = Math.min(containerWidth / viewport.width, 1.5) // Max scale of 1.5
        const scaledViewport = this.pdfPage.getViewport({ scale: scale * this.zoomLevel })

        console.log('Scaled viewport size:', scaledViewport.width, 'x', scaledViewport.height, 'scale:', scale * this.zoomLevel)

        // Clear canvas before rendering
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height)

        this.canvas.width = scaledViewport.width
        this.canvas.height = scaledViewport.height

        // Render page
        const renderTask = this.pdfPage.render({
          canvasContext: this.context,
          viewport: scaledViewport
        })

        await renderTask.promise
        console.log('Page rendered successfully:', pageNumber)

      } catch (error) {
        console.error('Error rendering page:', error)
        this.loadError = 'Failed to render page: ' + (error.message || 'Unknown error')
      }
    },

    async nextPage() {
      if (this.role === 'teacher' && this.currentPage < this.totalPages) {
        const newPage = this.currentPage + 1
        try {
          await this.changePresentationPage({
            webinarId: this.webinarId,
            page: newPage
          })
        } catch (error) {
          console.error('Error changing page:', error)
        }
      }
    },

    async previousPage() {
      if (this.role === 'teacher' && this.currentPage > 1) {
        const newPage = this.currentPage - 1
        try {
          await this.changePresentationPage({
            webinarId: this.webinarId,
            page: newPage
          })
        } catch (error) {
          console.error('Error changing page:', error)
        }
      }
    },

    zoomIn() {
      if (this.zoomLevel < 2.0) {
        this.zoomLevel = Math.min(this.zoomLevel + 0.2, 2.0)
      }
    },

    zoomOut() {
      if (this.zoomLevel > 0.5) {
        this.zoomLevel = Math.max(this.zoomLevel - 0.2, 0.5)
      }
    },

    retryLoad() {
      this.loadPDF()
    },

    clearPDF() {
      this.pdfDocument = null
      this.pdfPage = null
      this.loadError = null

      if (this.canvas && this.context) {
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height)
      }
    },

    // Handle window resize
    onWindowResize() {
      if (this.currentPage && this.pdfDocument) {
        setTimeout(() => {
          this.renderPage(this.currentPage)
        }, 100)
      }
    },

    scrollToUpload() {
      // Emit event to parent component to scroll to upload section
      this.$emit('scroll-to-upload')
    }
  },

  mounted() {
    // Add resize listener
    window.addEventListener('resize', this.onWindowResize)

    // Load PDF if presentation is already available
    if (this.currentPresentation) {
      this.loadPDF()
    }
  },

  beforeDestroy() {
    // Cleanup
    window.removeEventListener('resize', this.onWindowResize)
    this.clearPDF()
  }
}
</script>

<style lang="scss" scoped>
.presentation-viewer {
  height: 100%;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;

  .viewer-header {
    background: rgba(255, 255, 255, 0.95);
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e3e3e3;
    flex-shrink: 0;

    h6 {
      color: #2c3e50;
      font-weight: 600;
    }
  }

  .viewer-content {
    flex: 1;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    .no-presentation {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    }

    .pdf-container {
      flex: 1;
      position: relative;
      overflow: hidden;

      .pdf-viewer-wrapper {
        width: 100%;
        height: 100%;
        overflow: auto;
        padding: 20px;
        display: flex;
        justify-content: center;

        .pdf-canvas {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          background: white;
          border-radius: 4px;
          transition: transform 0.2s ease;
        }
      }

      .pdf-loading {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(248, 249, 250, 0.9);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
      }
    }

    .student-info {
      padding: 0.5rem 1rem;
      background: rgba(255, 255, 255, 0.9);
      border-top: 1px solid #e3e3e3;
      flex-shrink: 0;
    }
  }
}

// Button group styling
.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

// Alert styling
.alert {
  margin: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  display: flex;
  align-items: center;

  .feather {
    flex-shrink: 0;
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .presentation-viewer {
    .viewer-header {
      .d-flex {
        flex-direction: column;
        align-items: stretch;
        gap: 0.5rem;

        > div:last-child {
          justify-content: center;
        }
      }
    }

    .pdf-container .pdf-viewer-wrapper {
      padding: 10px;
    }
  }
}
</style>